import React, { useEffect, useState } from "react";
import { Coins, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CreditProductResponse, formatCreditAmount } from "@packages/global";
import { PageHeader, StatusPill } from "@packages/ui";
import { paymentsApi } from "../../services/user";
import { useDialog } from "../../hooks/useDialog";
import { HomeFooter } from "../home/HomeFooter";
import { HomeHeader } from "../home/HomeHeader";
import CreditPurchaseDialog from "./CreditPurchaseDialog";

const CreditPackagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { alert } = useDialog();
  const [products, setProducts] = useState<CreditProductResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<CreditProductResponse | null>(null);

  const loadProducts = async () => {
    setLoading(true);
    try {
      setProducts(await paymentsApi.publicProducts());
    } catch (error: any) {
      await alert(error?.response?.data?.message ?? "套餐加载失败", "加载失败", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProducts();
  }, []);

  const goToDownload = () => navigate("/#download");
  const openProductPurchase = (product: CreditProductResponse) => {
    setSelectedProduct(product);
    setPurchaseOpen(true);
  };
  const closePurchase = () => {
    setPurchaseOpen(false);
    setSelectedProduct(null);
  };

  return (
    <main className="home-landing min-h-screen overflow-x-hidden bg-bg-page text-text-primary">
      <HomeHeader />
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-10 sm:px-6 lg:py-12">
        <PageHeader
          eyebrow="Packages"
          title="积分套餐"
          description="套餐积分可用于模型对话、图片生成、联网检索等能力，购买后进入当前登录账号。"
        />

        <section className="grid gap-4 md:grid-cols-3">
          {loading ? (
            <div className="rounded-[12px] border border-border-subtle bg-bg-surface p-6 text-[13px] text-text-muted md:col-span-3">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              正在加载套餐...
            </div>
          ) : products.length === 0 ? (
            <div className="rounded-[12px] border border-border-subtle bg-bg-surface p-6 text-[13px] text-text-muted md:col-span-3">
              暂无可购买套餐。
            </div>
          ) : (
            products.map((product) => (
              <article key={product.id} className="flex min-h-[250px] flex-col rounded-[12px] border border-border-subtle bg-bg-surface p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-[18px] font-semibold text-text-primary">{product.name}</h2>
                    <p className="mt-2 text-[13px] leading-6 text-text-muted">{product.description}</p>
                  </div>
                  {product.badge && <StatusPill tone="success">{product.badge}</StatusPill>}
                </div>
                <div className="mt-7">
                  <div className="flex items-end gap-2">
                    <span className="font-mono text-[34px] font-semibold leading-none text-text-primary">{formatCreditAmount(product.credits)}</span>
                    <span className="pb-1 text-[12px] text-text-muted">积分</span>
                  </div>
                  <div className="mt-2 text-[12px] text-text-muted">有效期 {product.validityDays} 天</div>
                </div>
                <div className="mt-auto flex items-end justify-between gap-3 pt-6">
                  <div>
                    <div className="text-[11px] text-text-muted">支付金额</div>
                    <div className="text-[22px] font-semibold text-primary-ink">¥{(product.amountCents / 100).toFixed(2)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openProductPurchase(product)}
                    className="inline-flex min-h-10 items-center gap-2 rounded-[9px] bg-action-primary px-4 text-[13px] font-semibold text-white hover:bg-action-primary-hover"
                  >
                    <Coins className="h-4 w-4" />
                    购买
                  </button>
                </div>
              </article>
            ))
          )}
        </section>

        <section className="rounded-[12px] border border-border-subtle bg-bg-surface p-5">
          <h2 className="text-[15px] font-semibold text-text-primary">购买说明</h2>
          <div className="mt-3 grid gap-3 text-[13px] leading-6 text-text-secondary md:grid-cols-3">
            <p>支付成功后积分会自动进入当前登录账号。</p>
            <p>积分有效期按套餐展示天数计算，到期未使用部分会自动过期。</p>
            <p>系统异常导致扣费但服务未完成时，会按规则退回积分。</p>
          </div>
        </section>
      </div>

      <CreditPurchaseDialog
        isOpen={purchaseOpen}
        products={products}
        loading={loading}
        selectedProduct={selectedProduct}
        onClose={closePurchase}
      />
      <HomeFooter onDownload={goToDownload} />
    </main>
  );
};

export default CreditPackagesPage;
